<script setup>
import {use} from 'echarts/core';
import {PieChart} from 'echarts/charts';
import {LegendComponent, TooltipComponent} from 'echarts/components';
import {SVGRenderer} from 'echarts/renderers';
import VChart from 'vue-echarts';
import {computed, onMounted, ref} from 'vue';
import {getTenantData} from '@/services/TenantService';
import {formatAmount} from '@/helper';

use([TooltipComponent, LegendComponent, PieChart, SVGRenderer]);

/**
 * Rozložení dluhopisů podle náhledu – prstencový graf bez popisků v grafu, pod ním legenda „zkrácený název (podíl %)“.
 * Sdílený PieChart.vue se nepoužívá: kreslí plný koláč s popisky uvnitř a jeho zobrazení bez tabulky má nulovou výšku.
 */
const props = defineProps({
  // položky z client-bond-report/composition: {name, value, currency, ...}
  items: {type: Array, default: () => []},
});

// první barva podle tenanta (jako ostatní grafy aplikace), další podle palety prototypu, pak barvy PieChart.vue
const primaryColor = ref('#004148');
const colors = computed(() => [
  primaryColor.value, '#7fb3b9', '#046e7a', '#be8549',
  '#1f8d99', '#295acc', '#431f99', '#bb29cc', '#991f5c', '#99741f',
]);

onMounted(() => {
  getTenantData().then(response => {
    if (response?.theme?.primaryColor) {
      primaryColor.value = response.theme.primaryColor;
    }
  });
});

const total = computed(() => props.items.reduce((sum, item) => sum + (Number(item.value) || 0), 0));

/**
 * Zkrácený název do legendy jako v náhledu („EAG XIV. 7,35 %/2029“): první slovo emitenta a část za pomlčkou.
 * Název bez pomlčky se nechá celý.
 */
function shortName(name) {
  const parts = String(name ?? '').split(/\s[–-]\s/);
  if (parts.length < 2) {
    return name ?? '';
  }
  return parts[0].split(' ')[0] + ' ' + parts[parts.length - 1];
}

const options = computed(() => {
  // legenda pracuje s názvy položek – podíl se dohledá podle plného názvu
  const shareByName = Object.fromEntries(props.items.map(item => [
    item.name,
    total.value > 0 ? Math.round((Number(item.value) || 0) / total.value * 100) : 0,
  ]));

  return {
    color: colors.value,
    tooltip: {
      trigger: 'item',
      formatter: param => param.name + '<br/>' + param.marker + ' ' + formatAmount(param.value, param.data.currency ?? 'CZK'),
    },
    legend: {
      bottom: 0,
      left: 'center',
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 8,
      textStyle: {fontSize: 12},
      formatter: name => shortName(name) + ' (' + (shareByName[name] ?? 0) + ' %)',
    },
    series: [{
      type: 'pie',
      radius: ['45%', '80%'],
      center: ['50%', '42%'],
      label: {show: false},
      labelLine: {show: false},
      data: props.items.map(item => ({name: item.name, value: Number(item.value) || 0, currency: item.currency})),
    }],
  };
});
</script>

<template>
  <div class="bond-composition-chart">
    <v-chart :option="options" autoresize/>
  </div>
</template>

<style scoped>
/* výška podle náhledu: prstenec cca 230 px + legenda pod ním */
.bond-composition-chart {
  height: 300px;
}
</style>
